import { EntityBase } from '@provision/entity-types';
import { useCallback, useMemo, useState } from 'react';

export type BulkSelectionMode = 'manual';

interface BulkSelectionState {
  mode: BulkSelectionMode;
  selectedIds: Set<string>;
}

const INITIAL_STATE: BulkSelectionState = {
  mode: 'manual',
  selectedIds: new Set(),
};

export const useBulkSelectionState = <T extends EntityBase>({
  records = [],
  totalRecords: _totalRecords,
}: {
  records: T[];
  totalRecords: number;
}) => {
  const [selection, setSelection] = useState<BulkSelectionState>(INITIAL_STATE);

  const clearSelection = useCallback(() => {
    setSelection(INITIAL_STATE);
  }, []);

  const isRecordSelected = useCallback(
    (id: string): boolean => selection.selectedIds.has(id),
    [selection],
  );

  const setRecordSelected = useCallback((id: string, selected: boolean) => {
    setSelection((prev) => {
      const next = new Set(prev.selectedIds);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return { ...prev, selectedIds: next };
    });
  }, []);

  const setAllVisibleSelected = useCallback(
    (selected: boolean) => {
      setSelection((prev) => {
        const next = new Set(prev.selectedIds);
        if (selected) {
          for (const record of records) {
            next.add(String(record.id));
          }
        } else {
          for (const record of records) {
            next.delete(String(record.id));
          }
        }
        return { ...prev, mode: 'manual', selectedIds: next };
      });
    },
    [records],
  );

  const isAllVisibleSelected = useMemo(() => {
    if (!records.length) {
      return false;
    }
    return records.every((record) => selection.selectedIds.has(String(record.id)));
  }, [records, selection]);

  const isSomeVisibleSelected = useMemo(() => {
    if (!records.length) {
      return false;
    }
    return records.some((record) => selection.selectedIds.has(String(record.id)));
  }, [records, selection]);

  const totalSelectedCount = useMemo(() => {
    return selection.selectedIds.size;
  }, [selection]);

  const selectedIds = useMemo(() => {
    return [...selection.selectedIds];
  }, [selection]);

  const selectedVisibleRecords = useMemo(() => {
    return records.filter((r) => isRecordSelected(String(r.id)));
  }, [records, isRecordSelected]);

  return {
    selection,
    clearSelection,
    isRecordSelected,
    setRecordSelected,
    setAllVisibleSelected,
    isAllVisibleSelected,
    isSomeVisibleSelected,
    totalSelectedCount,
    selectedIds,
    selectedVisibleRecords,
  };
};
